import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, DataTable, FilterBar, FormField, FormSelect, TextInput, PageHeader, Toggle, StatusBadge, EmptyState, SettingsSection } from '@/components/ui';
import { AdminScripts } from './Scripts';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { apiClient } from '@/shared/api/client';
import { adminApi, type KeywordAlerts, type HoldSound, type HubWebhook, type HubAutomation, type HubCatalog, type SmtpSettings, type SmtpInput, type FireberrySettings } from '@/shared/api/adminApi';
import { FireberrySettingsSection } from './settings/FireberrySettingsSection';
import { SettingsSubNav } from './settings/SettingsSubNav';
import { StaffCenter } from './StaffCenter';
import { TaggingCenter } from './TaggingCenter';
import { toast } from '@/components/ui/Toast';
import { KeywordAlertsSection } from './KeywordAlertsSection';
import { HoursTab } from './Hours';
import { legacySettingsTabsEnabled } from '../lib/legacySettingsTabs';
import { HoldSoundSection } from './HoldSoundSection';
import { MohLibrarySection } from './MohLibrarySection';
import { WebhooksTab } from './Integrations/WebhooksTab';
import { ApiKeysTab } from './Integrations/ApiKeysTab';
import { AutomationsTab } from './Integrations/AutomationsTab';
import { CrmKlientoCard } from './Integrations/CrmKlientoCard';
import { LEAD_API_DOCS, LEAD_API_BASE } from './settings/leadApiDocs';
import { ApiTesterCard, ApiInspectorCard } from './settings/ApiToolsCards';
// הגדרות מכשירי אודיו — אותו component של הנציגים (פר-דפדפן, store משותף),
// כך שהאדמין מכוון את הסופטפון של עצמו מאותו מקום שבו שאר ההגדרות.
import { AudioDeviceSettings } from '../../agent/components/AudioDeviceSettings';

interface DidOption { id: number; number: string; label?: string | null }
interface Telephony { per_agent_dids: boolean; shared_did_id: number | null; dids: DidOption[] }
type WrapupMode = 'modal' | 'sidebar' | 'off';
interface SettingsData {
  company: { name?: string; slug?: string; timezone?: string; currency?: string; locale?: string; sip_domain?: string; branding?: Record<string, unknown> | null; agent_wrapup_mode?: WrapupMode; hold_sound?: HoldSound };
  telephony: Telephony;
  keyword_alerts?: KeywordAlerts;
  smtp?: SmtpSettings;
  fireberry?: FireberrySettings;
}

const TIMEZONES = ['Asia/Jerusalem', 'Europe/London', 'America/New_York', 'UTC'];
const CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP'];

type SettingsTab = 'company' | 'staff' | 'tagging' | 'telephony' | 'audio' | 'alerts' | 'hours' | 'webhooks' | 'api' | 'automations' | 'leads' | 'scripts';

/** אפיון 6.17: "מה שאינו נכנס להגדרות: התראות ודואר, שעות מוקד כטאב נפרד,
 *  תסריטים, טאב לידים כפול, צ'אט צוות." מוסתרות מאחורי דגל, לא נמחקות. */
const LEGACY_TABS: SettingsTab[] = ['alerts', 'hours', 'leads', 'scripts'];
// 07/09/2026: נציגים ותיוג ירדו מהתפריט הראשי — חיים כאן כלשוניות.
const SPEC_TABS: SettingsTab[] = ['company', 'staff', 'tagging', 'telephony', 'audio', 'webhooks', 'api', 'automations'];

/** סדר התצוגה נשמר גם כשהדגל דלוק, כדי שהמסך לא יזוז למי שמדליק אותו. */
const ALL_TABS: SettingsTab[] = ['company', 'staff', 'tagging', 'telephony', 'audio', 'alerts', 'hours', 'webhooks', 'api', 'automations', 'leads', 'scripts'];
const SETTINGS_TABS: SettingsTab[] = ALL_TABS;

const TAB_LABELS: Record<SettingsTab, string> = {
  company: 'חברה',
  staff: 'נציגים',
  tagging: 'תיוג וסיום שיחה',
  telephony: 'טלפוניה',
  audio: 'אודיו',
  alerts: 'התראות ודואר',
  hours: 'שעות',
  webhooks: 'Webhooks',
  api: 'API',
  automations: 'אוטומציות',
  leads: 'לידים',
  scripts: 'תסריטים',
};

function visibleSettingsTabs(): SettingsTab[] {
  return legacySettingsTabsEnabled() ? ALL_TABS : ALL_TABS.filter((t) => SPEC_TABS.includes(t));
}

export function AdminSettings() {
  const [data, setData] = useState<SettingsData | null>(null);
  // הלשונית נשמרת ב-URL (?tab=) כדי שהנתיב הישן /admin/scripts יוכל להפנות לכאן.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as SettingsTab | null;
  const tab: SettingsTab = requested && SETTINGS_TABS.includes(requested) ? requested : 'company';
  const setTab = (id: SettingsTab) => setParams(id === 'company' ? {} : { tab: id }, { replace: true });

  // מרכז האינטגרציות (Hub) — נטען כאן ומחולק ללשוניות Webhooks/אוטומציות.
  const [webhooks, setWebhooks] = useState<HubWebhook[]>([]);
  const [webhookCatalog, setWebhookCatalog] = useState<Record<string, string>>({});
  const [automations, setAutomations] = useState<HubAutomation[]>([]);
  const [catalog, setCatalog] = useState<HubCatalog>({ events: {}, actions: {}, operators: [] });

  const load = useCallback(() => {
    void adminApi.settings().then((d) => setData(d as SettingsData)).catch(() => toast('שגיאה בטעינת הגדרות', 'danger'));
  }, []);
  const loadWebhooks = useCallback(() => {
    void adminApi.hubWebhooks().then((r) => { setWebhooks(r.data); setWebhookCatalog(r.catalog); }).catch(() => toast('שגיאה בטעינת Webhooks', 'danger'));
  }, []);
  const loadAutomations = useCallback(() => {
    void adminApi.hubAutomations().then(setAutomations).catch(() => toast('שגיאה בטעינת אוטומציות', 'danger'));
  }, []);

  useEffect(() => { load(); }, [load]);
  // מוזרם פעם אחת — הלשוניות מציגות את הנתונים ומרעננות בעצמן בשינוי.
  useEffect(() => {
    loadWebhooks();
    loadAutomations();
    void adminApi.hubCatalog().then(setCatalog).catch(() => { /* קטלוג אופציונלי */ });
  }, [loadWebhooks, loadAutomations]);

  const nav = (
    <SettingsSubNav
      items={visibleSettingsTabs().map((id) => ({
        id,
        label: TAB_LABELS[id],
        count: id === 'webhooks' ? webhooks.length : id === 'automations' ? automations.length : undefined,
      }))}
      value={tab}
      onChange={(id) => setTab(id as SettingsTab)}
    />
  );

  // עיצוב v2: תת-תפריט אנכי בצד ועמודת תוכן לידו. כל לשונית כבר בנויה
  // מכרטיסים משלה (SettingsSection / Card), ולכן אין כרטיס עוטף נוסף.
  const content = !data?.company ? <Card>טוען…</Card> : (
    <>
      {tab === 'company' && <CompanyTab company={data.company} fireberry={data.fireberry} onChanged={load} onAutomationsChanged={loadAutomations} />}
      {tab === 'staff' && <StaffCenter embedded initialTab={params.get('sub') ?? undefined} />}
      {tab === 'tagging' && <TaggingCenter embedded initialTab={params.get('sub') ?? undefined} />}
      {tab === 'telephony' && <TelephonyTab telephony={data.telephony} holdSound={data.company.hold_sound} onChanged={load} />}
      {tab === 'audio' && <AudioTab />}
      {tab === 'alerts' && <AlertsTab keywordAlerts={data.keyword_alerts} smtp={data.smtp} onChanged={load} />}
      {tab === 'hours' && <HoursTab />}
      {tab === 'webhooks' && <WebhooksTab webhooks={webhooks} catalog={webhookCatalog} onChanged={loadWebhooks} />}
      {tab === 'api' && <ApiTab />}
      {tab === 'automations' && <AutomationsTab automations={automations} catalog={catalog} onChanged={loadAutomations} />}
      {tab === 'leads' && <LeadsTab />}
      {tab === 'scripts' && <AdminScripts embedded />}
    </>
  );

  return (
    <div style={{ padding: 'var(--gap)', maxWidth: 1280 }}>
      <PageHeader title="הגדרות" />
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {nav}
        {/* key=tab: כל מעבר לשונית מריץ מחדש את כניסת התוכן (gcUp .45s כמו בעיצוב). */}
        <div
          key={tab}
          className="anim-up"
          style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, animationDuration: '.45s' }}
        >
          {content}
        </div>
      </div>
    </div>
  );
}

/**
 * לשונית "חברה": זהות הארגון (שם/שפה/זמן/מטבע), מצב סיכום שיחה לנציגים
 * וקישורי פעולות מהירות. טלפוניה/מספרים והתראות/דואר עברו ללשוניות ייעודיות.
 * כל השדות כאן נשמרים יחד בכפתור "שמור" התחתון.
 */
function CompanyTab({ company, fireberry, onChanged, onAutomationsChanged }: {
  company: SettingsData['company'];
  fireberry?: FireberrySettings;
  onChanged: () => void;
  onAutomationsChanged?: () => void;
}) {
  const [name, setName] = useState(company.name ?? '');
  const [locale, setLocale] = useState(company.locale ?? 'he');
  const [timezone, setTimezone] = useState(company.timezone ?? 'Asia/Jerusalem');
  const [currency, setCurrency] = useState(company.currency ?? 'ILS');
  const branding = (company.branding ?? {}) as Record<string, unknown>;
  const [paymentLink, setPaymentLink] = useState(String(branding.payment_link ?? ''));
  const [calendarLink, setCalendarLink] = useState(String(branding.calendar_link ?? ''));
  const [wrapupMode, setWrapupMode] = useState<WrapupMode>(company.agent_wrapup_mode ?? 'modal');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateCompany({
        name, locale, timezone, currency,
        agent_wrapup_mode: wrapupMode,
        // Preserve any other branding keys (logo/colors) while updating the links.
        branding: { ...branding, payment_link: paymentLink.trim(), calendar_link: calendarLink.trim() },
      });
      toast('נשמר', 'ok');
      onChanged();
    } catch {
      toast('שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <SettingsSection title="פרטי הארגון" icon="settings" description="שם, שפה, אזור זמן ומטבע של המרכזייה.">
        <div className="stack">
          <FormField label="שם החברה"><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
          {company.slug && <FormField label="מזהה"><TextInput value={company.slug} disabled dir="ltr" /></FormField>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--gap)' }}>
            <FormField label="שפה">
              <FormSelect value={locale} onChange={(e) => setLocale(e.target.value)}>
                <option value="he">עברית</option>
                <option value="en">English</option>
              </FormSelect>
            </FormField>
            <FormField label="אזור זמן">
              <FormSelect value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </FormSelect>
            </FormField>
            <FormField label="מטבע">
              <FormSelect value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </FormSelect>
            </FormField>
          </div>
          {company.sip_domain && <FormField label="SIP Domain"><TextInput value={company.sip_domain} disabled dir="ltr" /></FormField>}
        </div>
      </SettingsSection>

      <SettingsSection title="פעולות מהירות בחייגן" icon="bolt" iconColor="var(--accent)"
        description="קישורים שייחשפו לנציג ככפתורי פעולה מהירה בזמן שיחה. השאר ריק כדי להסתיר.">
        <div className="stack">
          <FormField label="קישור סליקה">
            <TextInput value={paymentLink} onChange={(e) => setPaymentLink(e.target.value)} dir="ltr" placeholder="https://..." />
          </FormField>
          <FormField label="קישור ליומן" hint="כתובת יומן/קביעת פגישות (Calendly, Google וכו').">
            <TextInput value={calendarLink} onChange={(e) => setCalendarLink(e.target.value)} dir="ltr" placeholder="https://..." />
          </FormField>
        </div>
      </SettingsSection>

      <SettingsSection title="סיכום שיחה לנציגים" icon="clipboard"
        description="איך מוצג לנציג חלון תיעוד השיחה (קוד סיום/הערות) בסוף כל שיחה.">
        <FormField
          label="מצב הצגה"
          hint={
            wrapupMode === 'modal' ? 'חלון סיכום קופץ וחוסם בסוף כל שיחה — הנציג חייב לתעד לפני שממשיך.'
            : wrapupMode === 'sidebar' ? 'הסיכום נפתח כמגירה בצד, בלי לחסום — הנציג יכול להמשיך לעבוד ולתעד מתי שנוח.'
            : 'הסיכום לא יוצג כלל לנציגים. שים לב: שיחות לא יתועדו (קוד סיום/הערות/חזרה לתזכורת) והדבר ישפיע על הדוחות.'
          }
        >
          <FormSelect value={wrapupMode} onChange={(e) => setWrapupMode(e.target.value as WrapupMode)}>
            <option value="modal">חלון קופץ (חוסם)</option>
            <option value="sidebar">מגירה בצד (לא חוסם)</option>
            <option value="off">לא מוצג</option>
          </FormSelect>
        </FormField>
      </SettingsSection>

      {/* חיבור Fireberry CRM — נשמר בכפתור שלו, לא ב-SaveBar של פרטי הארגון. */}
      <FireberrySettingsSection initial={fireberry} onAutomationsChanged={onAutomationsChanged} />

      <SaveBar saving={saving} onSave={save} />
    </div>
  );
}

/**
 * לשונית "טלפוניה": מדיניות מספר יוצא (Caller ID) + מוזיקת המתנה (צליל בודד
 * וספרייה). מדיניות ה-DID נשמרת בכפתור התחתון; סקציות הצליל שומרות בעצמן.
 */
function TelephonyTab({ telephony, holdSound, onChanged }: { telephony: Telephony; holdSound?: HoldSound; onChanged: () => void }) {
  const [perAgent, setPerAgent] = useState(telephony.per_agent_dids);
  const [sharedDidId, setSharedDidId] = useState<string>(telephony.shared_did_id ? String(telephony.shared_did_id) : '');
  const [saving, setSaving] = useState(false);
  const didLabel = (d: DidOption) => d.label ? `${d.number} — ${d.label}` : d.number;

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateTelephony({
        per_agent_dids: perAgent,
        shared_did_id: perAgent ? null : (sharedDidId ? Number(sharedDidId) : null),
      });
      toast('נשמר', 'ok');
      onChanged();
    } catch {
      toast('שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      {/* ── מספרים יוצאים (Caller ID) — סקציה בולטת ─────────────────────── */}
      <DidPolicySection
        perAgent={perAgent}
        setPerAgent={setPerAgent}
        sharedDidId={sharedDidId}
        setSharedDidId={setSharedDidId}
        dids={telephony.dids}
        didLabel={didLabel}
      />

      <SaveBar saving={saving} onSave={save} label="שמור מדיניות מספרים" />

      {/* Music-on-hold upload — its own endpoints, saved independently. */}
      <HoldSoundSection initial={holdSound} />

      {/* ספריית מוזיקת המתנה — כמה קבצים, בחירה גלובלית/פר-תור, שורדים deploy. */}
      <MohLibrarySection />

      {/* 28/09/2026 (עיצוב v2): "מספרים" חזר לתפריט הראשי כמסך עצמאי —
          כאן נשאר רק קישור אליו, כדי שהטבלה לא תופיע בשני מקומות. */}
      <div className="card" style={{ padding: 'var(--pad-card)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>מספרי הטלפון של המרכזייה</div>
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>ניהול המספרים, הניתוב וההקלטה עבר למסך "מספרים".</div>
        </div>
        <Link to="/admin/dids" className="btn">למסך מספרים</Link>
      </div>
    </div>
  );
}

/**
 * לשונית "התראות ודואר": דואר יוצא (SMTP) + התראות מילות מפתח. כל אחת נשמרת
 * בכפתור שלה (self-saving), ולכן אין כפתור שמירה תחתון משותף.
 */
function AlertsTab({ keywordAlerts, smtp, onChanged }: { keywordAlerts?: KeywordAlerts; smtp?: SmtpSettings; onChanged: () => void }) {
  return (
    <div className="stack">
      {/* דואר יוצא (SMTP) — פרטי שרת דואר של Google לשליחת אימיילים מאוטומציות. */}
      <SmtpSettingsSection initial={smtp} />

      {/* התראות מילות מפתח (מילים + נציגים לניטור) — נשמר בנפרד. המנוע
          עצמו נקבע ע"י הסופר-אדמין פר-מרכזיה. */}
      <KeywordAlertsSection value={keywordAlerts} onChanged={onChanged} />
    </div>
  );
}

/** Sticky-feeling save row used by tabs whose fields persist via one button. */
function SaveBar({ saving, onSave, label }: { saving: boolean; onSave: () => void; label?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <button type="button" className="btn primary" onClick={onSave} disabled={saving}>
        {saving ? 'שומר…' : (label ?? 'שמור')}
      </button>
    </div>
  );
}

/**
 * Highlighted "outbound caller-id policy" block, shown prominently inside the
 * company tab: a toggle for per-agent vs shared numbers, plus (in shared mode)
 * the primary-number picker.
 */
function DidPolicySection({
  perAgent, setPerAgent, sharedDidId, setSharedDidId, dids, didLabel,
}: {
  perAgent: boolean;
  setPerAgent: (v: boolean) => void;
  sharedDidId: string;
  setSharedDidId: (v: string) => void;
  dids: DidOption[];
  didLabel: (d: DidOption) => string;
}) {
  return (
    <SettingsSection
      title="מספרים יוצאים (Caller ID)"
      icon="phoneOut"
      emphasized
      description="איזה מספר מוצג ללקוח כשנציג מתקשר החוצה."
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '10px 12px', background: 'var(--surface)',
          borderRadius: 'var(--r-2)', border: '1px solid var(--line)',
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>מספר אישי לכל נציג</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            {perAgent ? 'דלוק — לכל נציג מספר משלו' : 'כבוי — מספר אחד משותף לכולם'}
          </div>
        </div>
        <Toggle checked={perAgent} onChange={setPerAgent} />
      </div>

      <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginTop: 8 }}>
        {perAgent
          ? 'כל נציג מחייג מהמספר שהוקצה לו במסך "נציגים". נציג ללא מספר מוקצה — חיוג יוצא ייחסם עד שיוקצה לו מספר.'
          : 'כל הנציגים מחייגים מאותו מספר משותף שנבחר למטה.'}
      </div>

      {!perAgent && (
        <div style={{ marginTop: 12 }}>
          <FormField label="המספר הראשי (משותף לכל הנציגים)">
            {dids.length === 0 ? (
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין מספרים מוגדרים. הוסף מספר במסך "מספרים" (DIDs).</div>
            ) : (
              <FormSelect value={sharedDidId} onChange={(e) => setSharedDidId(e.target.value)}>
                <option value="">המספר הראשון (ברירת מחדל)</option>
                {dids.map((d) => <option key={d.id} value={d.id}>{didLabel(d)}</option>)}
              </FormSelect>
            )}
          </FormField>
        </div>
      )}

      {perAgent && (
        <div style={{ marginTop: 12, padding: 10, background: 'var(--surface)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)', border: '1px solid var(--line)' }}>
          הקצאת מספר לכל נציג מתבצעת במסך <strong>נציגים</strong> ← עריכת נציג ← "מספר יוצא".
        </div>
      )}
    </SettingsSection>
  );
}

/**
 * דואר יוצא (SMTP) פר-מרכזיה — פרטי שרת דואר של Google (או אחר) לשליחת
 * אימיילים מאוטומציות. הסיסמה (App Password של Gmail) מוצפנת בשרת ולא מוחזרת.
 * כפתור "שלח מייל בדיקה" מנסה חיבור אמיתי לפני שמסתמכים על ההגדרות.
 */
function SmtpSettingsSection({ initial }: { initial?: SmtpSettings }) {
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [host, setHost] = useState(initial?.host ?? 'smtp.gmail.com');
  const [port, setPort] = useState(String(initial?.port ?? 587));
  const [encryption, setEncryption] = useState<'tls' | 'ssl' | 'none'>(initial?.encryption ?? 'tls');
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState(initial?.from_address ?? '');
  const [fromName, setFromName] = useState(initial?.from_name ?? '');
  const [passwordSet, setPasswordSet] = useState(initial?.password_set ?? false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const payload = (): SmtpInput => ({
    enabled, host: host.trim(), port: Number(port) || 587, encryption,
    username: username.trim(), from_address: fromAddress.trim(), from_name: fromName.trim(),
    ...(password ? { password } : {}),
  });

  async function save() {
    if (!host.trim() || !username.trim() || !fromAddress.trim()) { toast('מלא host, שם משתמש וכתובת שולח', 'danger'); return; }
    if (!password && !passwordSet) { toast('יש להזין סיסמה (App Password של Gmail)', 'danger'); return; }
    setSaving(true);
    try {
      const res = await adminApi.updateSmtp(payload());
      setPasswordSet(res.password_set);
      setPassword('');
      toast('נשמר', 'ok');
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה', 'danger');
    } finally { setSaving(false); }
  }

  async function test() {
    if (!host.trim() || !username.trim()) { toast('מלא host ושם משתמש לפני בדיקה', 'danger'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.testSmtp(payload());
      setTestResult(res);
    } catch {
      setTestResult({ ok: false, message: 'שגיאה בבדיקת החיבור' });
    } finally { setTesting(false); }
  }

  async function clear() {
    setSaving(true);
    try {
      await adminApi.deleteSmtp();
      setEnabled(false); setPassword(''); setPasswordSet(false); setTestResult(null);
      toast('הוסר — חזרה למייל המערכתי', 'ok');
    } catch { toast('שגיאה בהסרה', 'danger'); } finally { setSaving(false); }
  }

  return (
    <SettingsSection
      title="דואר יוצא (SMTP)"
      icon="mail"
      selfSaving
      badge={passwordSet ? { label: 'מוגדר', tone: 'ok' } : undefined}
      description={
        <>
          פרטי שרת הדואר לשליחת אימיילים מאוטומציות. עבור Gmail: הפעל אימות דו-שלבי וצור{' '}
          <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>App Password</a>{' '}
          (סיסמה ייעודית לאפליקציה) — לא סיסמת החשבון הרגילה.
        </>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', border: '1px solid var(--line)', marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>שליחת מיילים מהמרכזיה</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            {enabled ? 'דלוק — אוטומציות ישלחו מייל דרך השרת הזה' : 'כבוי — פעולת "שליחת אימייל" באוטומציות תהיה נעולה'}
          </div>
        </div>
        <Toggle checked={enabled} onChange={setEnabled} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--gap)' }}>
        <FormField label="שרת (host)"><TextInput value={host} onChange={(e) => setHost(e.target.value)} dir="ltr" placeholder="smtp.gmail.com" /></FormField>
        <FormField label="פורט"><TextInput type="number" value={port} onChange={(e) => setPort(e.target.value)} dir="ltr" placeholder="587" /></FormField>
        <FormField label="הצפנה">
          <FormSelect value={encryption} onChange={(e) => setEncryption(e.target.value as 'tls' | 'ssl' | 'none')}>
            <option value="tls">TLS (587)</option>
            <option value="ssl">SSL (465)</option>
            <option value="none">ללא</option>
          </FormSelect>
        </FormField>
      </div>
      <FormField label="שם משתמש (כתובת Gmail)"><TextInput value={username} onChange={(e) => setUsername(e.target.value)} dir="ltr" placeholder="you@gmail.com" /></FormField>
      <FormField label={passwordSet ? 'סיסמה / App Password (הזן כדי להחליף)' : 'סיסמה / App Password'} hint={passwordSet ? 'כבר נשמרה סיסמה. השאר ריק כדי לשמור עליה.' : undefined}>
        <TextInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" placeholder={passwordSet ? '••••••••••••' : 'App Password בן 16 תווים'} />
      </FormField>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--gap)' }}>
        <FormField label="כתובת השולח (From)"><TextInput value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} dir="ltr" placeholder="you@gmail.com" /></FormField>
        <FormField label="שם השולח"><TextInput value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="שם העסק" /></FormField>
      </div>

      {testResult && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 'var(--r-2)', marginTop: 8, fontSize: 'var(--t-sm)', background: 'var(--surface-2)', border: `1px solid ${testResult.ok ? 'var(--ok, var(--line))' : 'var(--danger)'}`, color: testResult.ok ? 'var(--ok)' : 'var(--danger)' }}>
          <Icon name={testResult.ok ? 'ok' : 'danger'} size={15} color={testResult.ok ? 'var(--ok)' : 'var(--danger)'} />
          <span>{testResult.message}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button type="button" className="btn primary" onClick={() => void save()} disabled={saving}>{saving ? 'שומר…' : 'שמור SMTP'}</button>
        <button type="button" className="btn" onClick={() => void test()} disabled={testing}>{testing ? 'בודק…' : 'שלח מייל בדיקה'}</button>
        {passwordSet && <button type="button" className="btn ghost" onClick={() => void clear()} disabled={saving} style={{ color: 'var(--danger)' }}>הסר הגדרות</button>}
      </div>
    </SettingsSection>
  );
}

// ── לשונית API: מפתחות (עם scopes + מפתח-פעם-אחת) + תיעוד סנכרון לידים ─────────
// ── אודיו: מיקרופון ואוזניות + בדיקות חיות ─────────────────────────────────────
// ההגדרה פר-דפדפן (localStorage) — חלה על הסופטפון של המשתמש שמול המסך.
function AudioTab() {
  return (
    <SettingsSection
      title="מיקרופון ואוזניות"
      icon="headphones"
      description="בחר את המיקרופון והאוזניות של הסופטפון ובדוק אותם בלייב — בבדיקת המיקרופון תדבר ותשמע את עצמך באוזניות שנבחרו (עם מד עוצמה), ולאוזניות יש צליל בדיקה. ההגדרה נשמרת בדפדפן זה וחלה על הסופטפון שלך במחשב זה."
    >
      <AudioDeviceSettings />
    </SettingsSection>
  );
}

function ApiTab() {
  return (
    <div className="stack" style={{ gap: 'var(--gap)' }}>
      {/* אינטגרציית CRM (Kliento): מצב החיבור וניתוק. OverviewTab הישן אינו מנותב, לכן הכרטיס יושב כאן. */}
      <CrmKlientoCard />
      <ApiKeysTab />
      <ApiTesterCard />
      <ApiInspectorCard />
      {/* התיעוד בכרטיס משלו — הלשונית יושבת ישירות על רקע המסך. */}
      <div className="card" style={{ padding: 'var(--pad-card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Icon name="book" size={16} color="var(--accent)" />
          <strong style={{ fontSize: 'var(--t-md)' }}>סנכרון לידים — תיעוד API</strong>
          <span style={{ flex: 1 }} />
          <DownloadDocsPdfButton />
        </div>
        <p style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', margin: '0 0 8px' }}>
          קלוט ועדכן לידים ממערכות חיצוניות (Make, n8n, טפסים). בסיס: <code dir="ltr">{LEAD_API_BASE}</code>
        </p>
        <DocsSections />
      </div>
    </div>
  );
}

// הורדת התיעוד המלא כ-PDF — השרת מרנדר mpdf RTL מאותם מקטעים (docs-pdf).
function DownloadDocsPdfButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post('/admin/docs-pdf', {
        title: 'סנכרון לידים — תיעוד API',
        sections: LEAD_API_DOCS.map((sec) => ({
          title: sec.title,
          intro: sec.intro,
          steps: sec.steps ?? [],
          code: (sec.code ?? []).map((c) => ({ label: c.label, body: c.body })),
          tip: sec.tip ?? null,
        })),
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `merkaziya-api-docs-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('שגיאה בהפקת ה-PDF', 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button className="btn" onClick={() => void download()} disabled={busy}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-xs)' }}>
      <Icon name="download" size={14} />
      {busy ? 'מפיק…' : 'הורד PDF'}
    </button>
  );
}

// מיפוי שמות lucide שבמסמכים לשמות האייקונים של האפליקציה (fallback לעצמם).
const DOC_ICON: Record<string, string> = {
  key: 'lock', download: 'download', 'refresh-cw': 'history', upload: 'upload',
  zap: 'bolt', workflow: 'tree', 'git-branch': 'tree',
};

// ── מקטעי תיעוד מעוצבים (מ-leadApiDocs.ts): אייקון+intro+steps+code[]+tip ──────
function DocsSections() {
  const [open, setOpen] = useState<string | null>(LEAD_API_DOCS[0]?.id ?? null);
  return (
    <div className="stack" style={{ gap: 8 }}>
      {LEAD_API_DOCS.map((sec) => {
        const isOpen = open === sec.id;
        return (
          <div key={sec.id} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-3, 12px)', overflow: 'hidden', background: 'var(--surface)' }}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : sec.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'start' }}
            >
              <span style={{ display: 'inline-flex', width: 28, height: 28, borderRadius: 'var(--r-2)', background: 'var(--accent-soft, var(--surface-2))', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={DOC_ICON[sec.icon] ?? sec.icon} size={15} color="var(--accent)" />
              </span>
              <strong style={{ fontSize: 'var(--t-sm)', flex: 1 }}>{sec.title}</strong>
              <Icon name={isOpen ? 'chevronD' : 'chevron'} size={14} color="var(--muted)" />
            </button>
            {isOpen && (
              <div className="stack" style={{ gap: 10, padding: '0 12px 12px' }}>
                <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.6 }}>{sec.intro}</p>

                {sec.steps && sec.steps.length > 0 && (
                  <ol style={{ margin: 0, paddingInlineStart: 20, display: 'grid', gap: 6, fontSize: 'var(--t-sm)', lineHeight: 1.5 }}>
                    {sec.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                )}

                {sec.code?.map((block, i) => <CodeBlock key={i} label={block.label} body={block.body} />)}

                {sec.tip && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderInlineStart: '3px solid var(--accent)', background: 'var(--accent-soft, var(--surface-2))', borderRadius: 'var(--r-2)' }}>
                    <span style={{ flexShrink: 0 }}>💡</span>
                    <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.5 }}>{sec.tip}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// בלוק קוד עם תווית וכפתור העתקה — <pre dir="ltr"> כהה.
function CodeBlock({ label, body }: { label: string; body: string }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)' }}>
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{label}</span>
        <button
          type="button"
          className="btn ghost"
          style={{ height: 24, padding: '0 6px' }}
          onClick={() => { void navigator.clipboard.writeText(body).then(() => toast('הועתק', 'ok')).catch(() => {}); }}
          title="העתק"
        >
          <Icon name="copy" size={13} />
        </button>
      </div>
      <pre
        dir="ltr"
        style={{
          margin: 0, padding: 12, background: '#0f172a', color: '#e2e8f0',
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', lineHeight: 1.5,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto',
        }}
      >
        {body}
      </pre>
    </div>
  );
}

// ── לשונית לידים: תצוגה מנוהלת (קריאה) + קישור לתיעוד הקליטה ──────────────────
interface LeadRow { id: number; name?: string | null; phone?: string | null; status?: string | null; source?: string | null; updated_at?: string | null }

function LeadsTab() {
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    void adminApi.leads(null, { q: search || undefined, status: status || undefined, per_page: 50 })
      .then((res) => setRows((res.data as LeadRow[]) ?? []))
      .catch(() => toast('שגיאה בטעינת לידים', 'danger'))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  const cols: DataTableColumn<LeadRow>[] = [
    { key: 'name', he: 'שם', render: (r) => r.name || '—' },
    { key: 'phone', he: 'טלפון', render: (r) => <span dir="ltr">{r.phone || '—'}</span> },
    { key: 'status', he: 'סטטוס', render: (r) => (r.status ? <StatusBadge tone="info" size="sm">{r.status}</StatusBadge> : '—') },
    { key: 'source', he: 'מקור', render: (r) => r.source || '—' },
    { key: 'updated_at', he: 'עודכן', render: (r) => (r.updated_at ? new Date(r.updated_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '—') },
  ];

  return (
    <div className="stack" style={{ gap: 'var(--gap)' }}>
      <div style={{ background: 'var(--accent-soft, var(--surface-2))', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, fontSize: 'var(--t-sm)' }}>
        קליטת לידים ממערכות חיצוניות מתבצעת דרך ה-API (לשונית <strong>API</strong> → "סנכרון לידים"). לניהול מלא (ייבוא/עריכה/שיוך לקמפיין) עבור למסך <strong>מאגר לידים</strong>.
      </div>
      <FilterBar
        search={search}
        onSearch={setSearch}
        onClear={() => { setSearch(''); setStatus(''); }}
        placeholder="חיפוש לפי שם או טלפון"
      >
        <TextInput value={status} onChange={(e) => setStatus(e.target.value)} placeholder="סטטוס" style={{ width: 140, height: 36 }} />
      </FilterBar>
      <Card padded={false}>
        <DataTable<LeadRow>
          columns={cols}
          rows={rows}
          loading={loading}
          emptyState={<EmptyState icon="users" title="אין לידים" description="לידים שייקלטו דרך ה-API יופיעו כאן." />}
        />
      </Card>
    </div>
  );
}
